import { amountToDisplayValue, Flex, Space, Spacing, Tag, TagColor, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useLocale } from '../../../../../../../../hooks/useLocale'
import { formatDate } from '../../../../../../../../utils'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { Separator } from '../Separator'
import { VoidedSection } from '../VoidedSection'

interface HeroSectionProps {
  additionalTag?: ReactElement | null
  amount: number | null
  contactName?: string
  currency?: string | null
  entryDate?: string | null
  source?: string
  status?: {
    color: TagColor
    label: string
  }
}

export const HeroSection = ({
  additionalTag,
  amount,
  contactName,
  currency,
  entryDate,
  source,
  status,
}: HeroSectionProps) => {
  const { t } = useTranslation()
  const { code } = useLocale()
  const { organization } = useUserOrganization()

  const { translationKey: typeName, isVoidedEntry, journalLabel } = useTransactionDrawerContext()
  const formattedAmount = amount !== null && amountToDisplayValue(amount)
  const formattedEntryDate = formatDate(entryDate, 'd MMM yyyy', code)

  const shouldRenderTags = Boolean(status || additionalTag || isVoidedEntry)

  const isFrenchOrganization = organization?.countryId === 'FR'
  const typeLabel = isFrenchOrganization && journalLabel ? journalLabel : typeName

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center">
      {isVoidedEntry && <VoidedSection />}
      <Space size="s24" />
      {shouldRenderTags && (
        <>
          <Flex gap={Spacing.S4}>
            {(isVoidedEntry || status) && (
              <Tag color={isVoidedEntry ? 'red' : status?.color} type="subtle">
                {isVoidedEntry ? t('transactions.table.voided') : status?.label}
              </Tag>
            )}
            {additionalTag}
          </Flex>
          <Space size="s8" />
        </>
      )}
      <Text as="h3" variant="h1" strikethrough={isVoidedEntry && !!formattedAmount}>
        {formattedAmount || '-'}
      </Text>
      <Space size="s4" />
      <Text variant="bodySM" colorVariant="discreet">
        {currency}
      </Text>
      <Space size="s24" />

      <Separator />

      <Space size="s16" />
      <Text>{typeLabel}</Text>
      <Flex>
        <Text variant="bodySM" colorVariant="discreet" alignment="center">
          {[source, contactName].filter(Boolean).join(' · ')}
        </Text>
      </Flex>
      {formattedEntryDate && (
        <Flex>
          <Text variant="bodySM" colorVariant="discreet">
            {t('ledger.details.entry_date')}: {formattedEntryDate}
          </Text>
        </Flex>
      )}
      <Space size="s16" />
    </Flex>
  )
}
